import {useState, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {authDataSelector} from '@/services/redux/selectors';
import {Text, Icon, Button} from '@/ui-kits';
import {NA, SpecialistProfileStatus} from '@/constants';
import {formatName, getInitials} from '@/utils/common_functions';
import {submitSpecialistProfileApprovalRequest} from '@/services/redux/slices/authSlice';
import {useDispatch} from 'react-redux';
import {useConfimationModal} from '@/hooks';
import {FileViewerModal} from '../common/FileViewerModal';

interface SpecialistProfileDetailsProps {
  onEdit: () => void;
}

export const SpecialistProfileDetails: React.FC<SpecialistProfileDetailsProps> = props => {
  const {onEdit} = props;
  const profileData = useSelector(authDataSelector);
  const dispatch = useDispatch();
  const {showModal} = useConfimationModal();

  // Get specialization label from stored label
  const getSpecializationLabel = () => {
    const specialistData = profileData as any;
    if (specialistData?.specialization_label) {
      return specialistData.specialization_label;
    }
    return NA;
  };

  // Get location label from stored label
  const getLocationLabel = () => {
    const specialistData = profileData as any;
    // First try to use the location_label from API response
    if (!specialistData?.is_hospital_specialist && specialistData?.location_label) {
      return specialistData.location_label;
    }
    else{
      return `${specialistData?.hospital_name ?? NA}, ${specialistData?.hospital_address ?? NA}`;
    }
    return NA;
  };

  // Parse JSON strings if they exist - handles arrays and JSON strings
  // Does NOT split on commas - treats comma-containing text as single entries
  const parseArrayField = (field: any): string[] => {
    if (!field) return [];

    // Helper function to flatten and process items
    const processItem = (item: any): string[] => {
      // If item is a string that starts with [, try to parse it as JSON array
      if (typeof item === 'string' && item.startsWith('[')) {
        try {
          const parsed = JSON.parse(item);
          // If parsed is an array, recursively process each item
          if (Array.isArray(parsed)) {
            return parsed.flatMap(processItem);
          }
          // If parsed is a string, return it as is (don't split by comma)
          if (typeof parsed === 'string') {
            return [parsed];
          }
        } catch {
          // If parsing fails, return item as is
          return [item];
        }
      }

      // If item is an array, recursively process
      if (Array.isArray(item)) {
        return item.flatMap(processItem);
      }

      // Return as single string item (never split by comma)
      return [String(item)];
    };

    // If it's already an array, process each item
    if (Array.isArray(field)) {
      return field.flatMap(processItem);
    }

    // If it's a string, try to parse it
    if (typeof field === 'string') {
      try {
        const parsed = JSON.parse(field);
        // If parsed is an array, process each item
        if (Array.isArray(parsed)) {
          return parsed.flatMap(processItem);
        }
        // If parsed is a string, process it
        if (typeof parsed === 'string') {
          return processItem(parsed);
        }
      } catch {
        // If parsing fails, treat as plain string and check for commas
        if (field.includes(',')) {
          return field
            .split(',')
            .map(s => s.trim())
            .filter(s => s.length > 0);
        }
        return [field];
      }
    }

    return [];
  };

  const awards_and_achievements = parseArrayField((profileData as any)?.awards_and_achievements);
  const areas_of_expertise = parseArrayField((profileData as any)?.areas_of_expertise);
  const profileStatus = (profileData as any)?.profile_status as SpecialistProfileStatus | undefined;

  // License file preview handling
  const licenseData = (profileData as any)?.license;
  const licenseFile = licenseData?.url ?? (profileData as any)?.license_file;
  const licenseNumber = licenseData?.liscence_number ?? licenseData?.license_number ?? (profileData as any)?.license_number;
  const licenseYear = licenseData?.license_year ?? (profileData as any)?.license_issue_year ?? (profileData as any)?.license_year;
  const [licenseImageError, setLicenseImageError] = useState(false);
  const [photoImageError, setPhotoImageError] = useState(false);
  const [signatureImageError, setSignatureImageError] = useState(false);
  const [licenseModalOpen, setLicenseModalOpen] = useState<boolean>(false);
  const isLicensePdf = useMemo(() => {
    if (!licenseFile || typeof licenseFile !== 'string') return false;
    return licenseFile.toLowerCase().endsWith('.pdf') || licenseFile.toLowerCase().includes('.pdf');
  }, [licenseFile]);
  const signatureFile = (profileData as any)?.specialist_signature ?? (profileData as any)?.signature;

  useEffect(() => {
    setLicenseImageError(false);
  }, [licenseFile]);

  useEffect(() => {
    setPhotoImageError(false);
  }, [profileData?.photo_url]);

  useEffect(() => {
    setSignatureImageError(false);
  }, [signatureFile]);

  // Show Edit Profile button when status is NOT AwaitingAdminApproval (3)
  const canEditProfile = ![SpecialistProfileStatus.AwaitingAdminApproval, SpecialistProfileStatus.ReSubmitted].includes(profileStatus ?? -1);

  // Show Submit for Approval button when status is EligibleForApproval (2) or ChangeRequested (4)
  const canSubmitForApproval = [
    SpecialistProfileStatus.EligibleForApproval,
    SpecialistProfileStatus.ProfileEdited,
    SpecialistProfileStatus.ReEditAfterReChangeRequested,
  ].includes(profileStatus ?? -1);
  // profileStatus === SpecialistProfileStatus.ChangeRequested;

  function handleEditProfile() {
    onEdit();
  }

  function handleSubmitConfirm() {
    dispatch(submitSpecialistProfileApprovalRequest());
  }

  function handleSubmitForApproval() {
    showModal({
      message: <Text className="!font-FigtreeSemiBold">Please make sure all details are correct before submitting</Text>,
      onConfirmHandler: handleSubmitConfirm,
      cancelButtonText: 'Go Back',
      confirmButtonText: 'Submit',
      primaryCta: 'confirm',
      showCloseButton: false,
    });
  }

  return (
    <>
      <div className="flex flex-col gap-6 xl:gap-10 w-full">
        <div className="bg-white rounded-xl shadow-[0px_4px_41.6px_-13px_rgba(0,0,0,0.15)] p-3 sm:p-6">
          <div className="flex flex-col gap-6 xl:gap-10 w-full">
            {/* Professional Photo Card */}
            <div className="bg-gray-50 rounded-xl border border-border p-3 sm:p-6 pb-10">
              <div className="mb-4 sm:mb-6">
                <div className="flex items-center justify-between gap-2 sm:gap-3">
                  <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-3xl">
                    Professional Photo
                  </Text>
                  {canEditProfile && (
                    <Button title="Edit Profile" variant="primary" size="md" onClick={handleEditProfile} className="min-w-[120px] sm:min-w-[180px] h-10" />
                  )}
                </div>
              </div>
              <div className="flex flex-col 2xl:flex-row items-center 2xl:items-start gap-6 2xl:gap-12 mt-16 2xl:mt-14">
                <div className="relative w-full max-w-[272px] h-[272px] rounded-lg bg-profile_bg_light_blue flex-shrink-0 flex items-center justify-center overflow-visible mx-auto 2xl:mx-0">
                  {profileData?.photo_url && profileData.photo_url.trim() !== '' && !photoImageError ? (
                    <img
                      src={profileData.photo_url}
                      alt="Professional Photo"
                      className="h-[305px] w-auto object-contain object-bottom -mt-8"
                      onError={() => setPhotoImageError(true)}
                    />
                  ) : (
                    <div className="flex items-center justify-center w-full h-full">
                      <Text variant="free" className="text-ui_blue text-6xl sm:text-7xl font-FigtreeBold">
                        {getInitials(profileData?.username ?? NA)}
                      </Text>
                    </div>
                  )}
                </div>
                <div className="flex-1 w-full">
                  <Text
                    variant="heading"
                    className="font-FigtreeBold text-ui_blue text-base text-center 2xl:text-left 2xl:text-4xl mb-6 sm:mb-8 2xl:mb-8 2xl:ml-16 leading-tight">
                    Dr. {formatName(profileData?.username ?? NA)}
                  </Text>
                  <div className="flex flex-col gap-6 2xl:ml-16">
                    <div className="flex items-center gap-4 sm:gap-6 w-full">
                      <div
                        style={{
                          filter: `brightness(0) saturate(100%) invert(31%) sepia(98%) saturate(1234%) hue-rotate(186deg) brightness(98%) contrast(87%)`,
                        }}>
                        <Icon type="email" className="w-5 h-5 flex-shrink-0" />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                        <Text variant="free" className="font-FigtreeMeduim text-ui_input_border_dark text-sm mb-1">
                          Email Address
                        </Text>
                        <Text variant="free" className="font-FigtreeSemiBold text-ui_blue text-base sm:text-lg break-words break-all">
                          {profileData?.email_id ?? NA}
                        </Text>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-6 w-full">
                      <div
                        style={{
                          filter: `brightness(0) saturate(100%) invert(31%) sepia(98%) saturate(1234%) hue-rotate(186deg) brightness(98%) contrast(87%)`,
                        }}>
                        <Icon type="phone" className="w-5 h-5 flex-shrink-0" />
                      </div>
                      <div className="flex flex-col min-w-0 flex-1 overflow-hidden">
                        <Text variant="free" className="font-FigtreeMeduim text-ui_input_border_dark text-sm mb-1">
                          Phone Number
                        </Text>
                        <Text variant="free" className="font-FigtreeSemiBold text-ui_blue text-base sm:text-lg break-words break-all">
                          {profileData?.phone ?? NA}
                        </Text>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 sm:gap-6 w-full">
                      <div
                        style={{
                          filter: `brightness(0) saturate(100%) invert(31%) sepia(98%) saturate(1234%) hue-rotate(186deg) brightness(98%) contrast(87%)`,
                        }}>
                        <Icon type="pin" className="w-5 h-5" />
                      </div>
                      <div className="flex flex-col min-w-0 w-full">
                        <Text variant="free" className="font-FigtreeMeduim text-ui_input_border_dark text-sm mb-1">
                          Location
                        </Text>
                        <Text variant="free" className="font-FigtreeSemiBold text-ui_blue text-base sm:text-lg break-words break-all w-full">
                          {getLocationLabel()}
                        </Text>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Information Card */}
            <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Icon type="stethoscope2" className="w-4 h-4 sm:w-5 sm:h-5" />
                <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
                  Professional Information
                </Text>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 sm:gap-x-20 gap-y-4 sm:gap-y-6">
                <div className="flex flex-col gap-4 sm:gap-6">
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="body" className="!font-FigtreeMeduim text-ui_input_border_dark text-lg mb-1">
                      Current Designation
                    </Text>
                    <Text variant="subtitle" className="font-FigtreeSemiBold text-ui_blue text-base">
                      {formatName((profileData as any)?.designation ?? NA)}
                    </Text>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="body" className="!font-FigtreeMeduim text-ui_input_border_dark text-lg mb-1">
                      Specialization
                    </Text>
                    <Text variant="subtitle" className="font-FigtreeSemiBold text-ui_blue text-base">
                      {getSpecializationLabel()}
                    </Text>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="body" className="!font-FigtreeMeduim text-ui_input_border_dark text-lg mb-1">
                      Years of Experience
                    </Text>
                    <Text variant="subtitle" className="font-FigtreeSemiBold text-ui_blue text-base">
                      {(profileData as any)?.years_of_experience?.toString() ?? NA}
                    </Text>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="body" className="!font-FigtreeMeduim text-ui_input_border_dark text-lg mb-1">
                      Education
                    </Text>
                    <Text variant="subtitle" className="font-FigtreeSemiBold text-ui_blue text-base">
                      {formatName((profileData as any)?.education ?? NA)}
                    </Text>
                  </div>
                </div>
                <div className="flex flex-col gap-4 sm:gap-6">
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="body" className="!font-FigtreeMeduim text-ui_input_border_dark text-lg mb-1">
                      License Number
                    </Text>
                    <Text variant="subtitle" className="font-FigtreeSemiBold text-ui_blue text-base">
                      {licenseNumber ?? NA}
                    </Text>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="body" className="!font-FigtreeMeduim text-ui_input_border_dark text-lg mb-1">
                      License Issue Year
                    </Text>
                    <Text variant="subtitle" className="font-FigtreeSemiBold text-ui_blue text-base">
                      {licenseYear ?? NA}
                    </Text>
                  </div>
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="body" className="!font-FigtreeMeduim text-ui_input_border_dark text-lg mb-1">
                      License Document
                    </Text>
                    {licenseFile && (
                      <div className="relative w-full max-w-[200px] h-[120px] sm:h-[150px] border border-dashed border-border rounded-lg overflow-hidden bg-white mt-2">
                        {!isLicensePdf && !licenseImageError ? (
                          <img src={licenseFile} alt="License" className="w-full h-full object-cover" onError={() => setLicenseImageError(true)} />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gray-50">
                            <Icon type="file" className="w-10 h-10" />
                            <Text variant="free" className="text-sm sm:text-base text-center px-4 text-ui_blue">
                              {isLicensePdf ? 'PDF Document' : 'Preview not available'}
                            </Text>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/45 pointer-events-none" />
                        <button
                          type="button"
                          onClick={() => setLicenseModalOpen(true)}
                          className="absolute left-1/2 -translate-x-1/2 bottom-14 h-6 px-4 border-white border-2 rounded-full text-white font-FigtreeSemiBold hover:opacity-80 transition-opacity">
                          View
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Professional Experience Card */}
            <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Icon type="business_center_logo" className="w-4 h-4 sm:w-5 sm:h-5" />
                <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
                  Professional Experience
                </Text>
              </div>
              <div className="ml-0 sm:ml-9">
                {(profileData as any)?.professional_experience ? (
                  <ul className="list-disc list-inside space-y-2">
                    {(profileData as any).professional_experience
                      .split('.')
                      .map((item: string) => item.trim())
                      .filter((item: string) => item.length > 0)
                      .map((item: string, index: number) => (
                        <li key={index} className="font-FigtreeMeduim text-ui_blue text-base">
                          {item}
                        </li>
                      ))}
                  </ul>
                ) : (
                  <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base">
                    {NA}
                  </Text>
                )}
              </div>
            </div>

            {/* Qualifications Card */}
            <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Icon type="graduation_icon" className="w-4 h-4 sm:w-5 sm:h-5" />
                <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-1 sm:ml-2">
                  Qualifications
                </Text>
              </div>
              <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base ml-0 sm:ml-9">
                {(profileData as any)?.qualifications ?? NA}
              </Text>
            </div>

            {/* Awards & Achievements and Areas of Expertise - Side by Side */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-start">
              {/* Awards & Achievements Card */}
              <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
                <div className="flex items-center gap-2 mb-4 sm:mb-6">
                  <Icon type="awards_logo" className="w-4 h-4 sm:w-5 sm:h-5" />
                  <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-2">
                    Awards & Achievements
                  </Text>
                </div>
                {awards_and_achievements.length > 0 ? (
                  <div className="space-y-2">
                    {awards_and_achievements.map((award, index) => (
                      <div key={index} className="bg-white rounded-lg p-3 sm:p-4">
                        <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base">
                          {award}
                        </Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base">
                      {NA}
                    </Text>
                  </div>
                )}
              </div>

              {/* Areas of Expertise Card */}
              <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
                <div className="flex items-center gap-2 mb-4 sm:mb-6">
                  <Icon type="expertise_logo" className="w-4 h-4 sm:w-5 sm:h-5" />
                  <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-2">
                    Areas of Expertise
                  </Text>
                </div>
                {areas_of_expertise.length > 0 ? (
                  <div className="space-y-2">
                    {areas_of_expertise.map((expertise, index) => (
                      <div key={index} className="bg-white rounded-lg p-3 sm:p-4">
                        <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base">
                          {expertise}
                        </Text>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-3 sm:p-4">
                    <Text variant="free" className="font-FigtreeMeduim text-ui_blue text-base">
                      {NA}
                    </Text>
                  </div>
                )}
              </div>
            </div>
            <div className="bg-ui_bg_sky rounded-lg p-3 sm:p-6 border border-border">
              <div className="flex items-center gap-2 mb-4 sm:mb-6">
                <Icon type="signature" className="w-4 h-4 sm:w-5 sm:h-5" />
                <Text variant="free" className="text-ui_blue font-FigtreeSemiBold text-lg sm:text-2xl ml-2">
                  Signature
                </Text>
              </div>
              {signatureFile ? (
                <div className="relative w-full max-w-[200px] h-[120px] sm:h-[150px] overflow-hidden ml-8 sm:ml-9">
                  {!signatureImageError ? (
                    <img src={signatureFile} alt="Signature" className="w-full h-full object-contain" onError={() => setSignatureImageError(true)} />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center gap-2">
                      <Icon type="file" className="w-10 h-10" />
                      <Text variant="free" className="text-sm sm:text-base text-center px-4 text-ui_blue">
                        Preview not available
                      </Text>
                    </div>
                  )}
                </div>
              ) : (
                <Text variant="subtitle" className="font-FigtreeSemiBold text-ui_blue text-base ml-8 sm:ml-9">
                  {NA}
                </Text>
              )}
            </div>
          </div>
        </div>
        {canSubmitForApproval && (
          <div className="flex justify-center">
            <Button title="Submit for Approval" variant="primary" size="lg" className="min-w-[200px] sm:min-w-[250px]" onClick={handleSubmitForApproval} />
          </div>
        )}
      </div>

      {licenseFile && (
        <FileViewerModal
          file={{
            id: 0,
            name: 'License',
            url: licenseFile,
          }}
          isOpen={licenseModalOpen}
          onClose={() => setLicenseModalOpen(false)}
          showDownloadButton
          isSecure={false}
        />
      )}
    </>
  );
};
